import React, { useState } from "react";
import { AxiosError } from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { FormModal } from "@/shared/ui/formModal";
import { Textarea } from "@/shared/ui/textarea";
import {
  getReadInsightsApiInsightsGetQueryKey,
  useCreateComplaintApiComplaintsPost,
} from "@/type-gen/api";

interface ReportProblemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ReportProblemDialog: React.FC<ReportProblemDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const queryClient = useQueryClient();
  const createComplaint = useCreateComplaintApiComplaintsPost();

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setMessage("");
      setError("");
    }
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    const trimmed = message.trim();
    if (!trimmed) {
      setError("Write a short note about the problem.");
      return;
    }
    setError("");
    try {
      await createComplaint.mutateAsync({ data: { message: trimmed } });
      await queryClient.invalidateQueries({
        queryKey: getReadInsightsApiInsightsGetQueryKey(),
      });
      setMessage("");
      onOpenChange(false);
    } catch (caught) {
      setError(readError(caught) ?? "Could not send that. Try again.");
    }
  };

  return (
    <FormModal
      open={open}
      onOpenChange={handleOpenChange}
      title="Report a problem"
      description="This goes to the JelloShot account. It is not visible to other people."
      submitText="Send"
      isLoading={createComplaint.isPending}
      onSubmit={() => {
        void handleSubmit();
      }}
    >
      <Textarea
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        placeholder="What went wrong?"
        maxLength={4000}
      />
      {error ? <p className="mt-2 text-sm text-destructive">{error}</p> : null}
    </FormModal>
  );
};

function readError(caught: unknown): string | null {
  if (!(caught instanceof AxiosError)) {
    return null;
  }
  const data: unknown = caught.response?.data;
  if (typeof data !== "object" || data === null || !("detail" in data)) {
    return null;
  }
  const detail = data.detail;
  return typeof detail === "string" ? detail : null;
}
